/** Resolve a day through our mounted header cells, including horizontal scroll. */
export function dateAtClientX(
  root: HTMLElement,
  clientX: number,
): string | null {
  for (const cell of root.querySelectorAll<HTMLElement>(
    '[data-absence-date-cell]',
  )) {
    const rect = cell.getBoundingClientRect();
    if (clientX >= rect.left && clientX < rect.right)
      return cell.dataset.date ?? null;
  }
  return null;
}

/** A bar is visual scope, not edit scope. The clicked day names its own entry. */
export function memberAtDate(
  members: Record<string, number> | undefined,
  date: string | null,
  fallbackId: number,
): number | null {
  if (!members) return fallbackId;
  if (date) return members[date] ?? null;
  return null;
}

/**
 * The contiguous run of marked days inside an aggregated bar.
 *
 * Returns the first index and how many days it covers, or null when nothing is
 * marked. Only a single run is described: the bar paints one band, and a
 * selection that skipped days inside a block would need more than a band --
 * it cannot arise from a click, which names one day.
 */
export function markedDayRun(
  memberIds: readonly number[],
  isMarked: (id: number) => boolean | undefined,
): { from: number; span: number } | null {
  const first = memberIds.findIndex((id) => isMarked(id));
  if (first === -1) return null;
  let span = 0;
  while (first + span < memberIds.length && isMarked(memberIds[first + span])) {
    span += 1;
  }
  return { from: first, span };
}

/**
 * One background layer shading the given spans of an aggregated bar.
 *
 * A bar is a single element covering days that are not all alike -- a weekend
 * inside a run takes a lighter shade than the weekdays around it -- so the
 * shade is painted across the part of the element those days occupy. The
 * spans are offsets from the bar's own left edge, measured from the header
 * cells, because the bar's width is whatever the renderer has clipped it to.
 *
 * One layer with a stop pair per span rather than a layer each, so a long
 * absence does not grow a background layer for every weekend it covers.
 *
 * Every stop carries an explicit position and the layer ends with a hard stop
 * out to 100%: a bare terminating `transparent 0` leans on the spec's
 * clamp-to-previous fix-up, which left the far end of a bar tinted when a band
 * sat at the near one. Null where there is nothing to shade, so the caller can
 * clear the property rather than paint an empty gradient.
 */
export function shadedSpansGradient(
  spans: readonly { left: number; right: number }[],
  colour: string,
): string | null {
  if (!spans.length) return null;
  const stops: string[] = [];
  let cursor = '0';
  for (const { left, right } of spans) {
    const from = `${left}px`;
    const to = `${right}px`;
    stops.push(`transparent ${cursor} ${from}`, `${colour} ${from} ${to}`);
    cursor = to;
  }
  stops.push(`transparent ${cursor} 100%`);
  return `linear-gradient(90deg,${stops.join(',')})`;
}
